import React, { createContext, useState, useMemo, useEffect, useContext, useCallback, memo, useRef, } from 'react';
import i18next from 'i18next';
import identity from 'lodash/identity';
import noop from 'lodash/noop';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE_CODE, DEFAULT_NAMESPACE } from './constants';
const initialDictionary = {
    __initial__: 'true',
};
const TranslationContext = createContext({
    dictionary: initialDictionary,
    language: DEFAULT_LANGUAGE_CODE,
    isInitialized: false,
    addToDictionary: noop,
    t: identity,
});
export const TranslationProvider = memo(({ children, language }) => {
    const i18nInstance = useRef(i18next.use(initReactI18next));
    const [isInitialized, setIsInitialized] = useState(false);
    const [dictionary, setDictionary] = useState(initialDictionary);
    useEffect(() => {
        i18nInstance.current.init({
            fallbackLng: language,
            resources: {
                [language]: {
                    [DEFAULT_NAMESPACE]: initialDictionary,
                },
            },
        }, (error) => {
            if (!error) {
                setIsInitialized(true);
            }
        });
    }, [language]);
    const addToDictionary = useCallback((labels) => {
        i18nInstance.current.addResources(language, DEFAULT_NAMESPACE, labels);
        setDictionary((prevDictionary) => ({
            ...prevDictionary,
            ...labels,
        }));
    }, [language]);
    const t = useCallback((key) => {
        if (dictionary) {
            return i18nInstance.current?.t(key);
        }
        return key;
    }, [dictionary]);
    const value = useMemo(() => ({
        dictionary: i18nInstance.current?.store?.data?.[language]?.[DEFAULT_NAMESPACE] ||
            dictionary,
        isInitialized,
        language,
        addToDictionary,
        t,
    }), [language, dictionary, isInitialized, addToDictionary, t]);
    return React.createElement(TranslationContext.Provider, { value: value }, children);
});
export const useTranslation = () => useContext(TranslationContext);
TranslationProvider.displayName = 'TranslationProvider';
